import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHead } from "@/components/Reveal";
import { GALLERY } from "@/lib/site";

const FILTERS = ["All", "Clinic Spaces", "Treatment Bays", "Rehab Gym", "Diagnostics & Equipment"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const list = filter === "All" ? GALLERY : GALLERY.filter((g) => g.cat === filter);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-testid="gallery-page" className="pt-[72px]">
      <Seo
        title="Clinic Gallery | Kamana Physiotherapy & Rehab Center, Gomti Nagar"
        description="Tour Kamana Physiotherapy & Rehab Center in Gomti Nagar, Lucknow — treatment bays, rehab gym, diagnostics and equipment inside our modern clinic."
      />

      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <p className="mono-label text-gold">Clinic Tour</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.06] max-w-3xl">
            Step inside a clinic built <span className="italic text-gold">for recovery.</span>
          </h1>
          <p className="mt-6 text-alabaster/70 max-w-2xl leading-relaxed">
            Private treatment bays, a fully-equipped rehabilitation gym and modern diagnostics —
            right in the heart of Gomti Nagar.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <div className="flex flex-wrap gap-2.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`text-xs sm:text-sm font-semibold rounded-full px-5 py-2.5 border transition-all duration-200 ${
                filter === f
                  ? "bg-teal-brand text-alabaster border-teal-brand"
                  : "bg-white text-inkmuted border-teal-brand/15 hover:border-teal-brand/50"
              }`}
            >
              {f === "All" ? "All Spaces" : f}
            </button>
          ))}
        </div>

        <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
          <AnimatePresence>
            {list.map((g, i) => (
              <motion.button
                key={g.src}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(g)}
                className="group relative block w-full mb-5 text-left break-inside-avoid"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  className="w-full h-auto rounded-2xl object-cover group-hover:opacity-90 transition-opacity duration-300"
                />
                <span className="absolute bottom-3 left-3 mono-label !text-[8px] bg-teal-obsidian/80 text-gold backdrop-blur rounded-full px-3 py-1.5">
                  {g.cat}
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>

        <Reveal>
          <p className="mt-4 text-center text-xs text-inkmuted">
            Sample clinic imagery — real photographs of our Gomti Nagar centre will be added shortly.
          </p>
        </Reveal>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-teal-obsidian/90 backdrop-blur-xl flex items-center justify-center p-6"
            data-testid="gallery-lightbox"
            onClick={() => setLightbox(null)}
          >
            <motion.img
              key={lightbox.src}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-h-[85vh] max-w-full rounded-2xl img-spotlight"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="gallery-lightbox-close"
              aria-label="Close"
              className="absolute top-6 right-6 h-11 w-11 rounded-full bg-alabaster/10 border border-alabaster/20 text-alabaster flex items-center justify-center hover:bg-alabaster/20 transition-colors"
            >
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
